import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Formik, Form as FormikForm } from 'formik';

import { Breadcrumb, Panel, NavLinkButton, Button, Alert } from '@shared/components/bootstrap';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikSelectFormGroup } from '@shared/components/fields/formik/formik_select_form_group';

import { PageHeader } from '@admin/components/helpers/page_header';
import { Spinner } from '@admin/components/spinner';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  Landing__Location__CityInput,
  LandingLocationCityQuery,
  useLandingLocationCityQuery,
  useLandingLocationCityUpsertMutation,
  useLandingLocationStatesQuery,
  useRegionsQuery,
  Landing__Location__City,
} from '@admin/schema';

const FormikRegionFormGroup: React.FC<{
  id: string;
  name: string;
  label: string;
}> = (props) => {
  const { data, loading } = useRegionsQuery({ client });

  return (
    <FormikSelectFormGroup disabled={loading} {...props}>
      <option value=""> - Region - </option>
      {data?.regions.map((region) => (
        <option key={region.id} value={region.id}>
          {region.name}
        </option>
      ))}
    </FormikSelectFormGroup>
  );
};

const FormikStateFormGroup: React.FC<{
  id: string;
  name: string;
  label: string;
}> = (props) => {
  const { data, loading } = useLandingLocationStatesQuery({ client });

  return (
    <FormikSelectFormGroup disabled={loading} {...props}>
      <option value=""> - State - </option>
      {data?.states.map((state) => (
        <option key={state.id} value={state.id}>
          {state.name}
        </option>
      ))}
    </FormikSelectFormGroup>
  );
};

type FormInput = Omit<Landing__Location__CityInput, 'latitude' | 'longitude'> & {
  latitude: string;
  longitude: string;
};

const Fields: React.FC<{
  city?: LandingLocationCityQuery['city'];
  onSave(city: Pick<Landing__Location__City, 'id'>): void;
}> = ({ city, onSave }) => {
  const [save, { loading, data }] = useLandingLocationCityUpsertMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.resource) onSave(result.resource);
    },
  });

  const onSubmit = (input: FormInput) => {
    if (input.latitude === '' || input.longitude === '') return;
    const latitude = Number(input.latitude);
    const longitude = Number(input.longitude);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return;

    save({
      variables: {
        id: city?.id,
        input: {
          ...input,
          latitude,
          longitude,
        },
      },
    });
  };

  return (
    <Formik<FormInput>
      initialValues={{
        name: city?.name ?? '',
        slug: city?.slug ?? '',
        latitude: city ? String(city.latitude) : '',
        longitude: city ? String(city.longitude) : '',
        regionID: city?.region.id ?? '',
        stateID: city?.state.id ?? '',
      }}
      onSubmit={onSubmit}
    >
      <FormikForm>
        <Panel>
          <Panel.Body>
            {data?.result?.error && <Alert style="danger">{data.result.error}</Alert>}
            <FormikStateFormGroup id="stateID" name="stateID" label="State:" />
            <FormikRegionFormGroup id="regionID" name="regionID" label="Region:" />
            <FormikInputFormGroup id="slug" name="slug" label="Slug:" />
            <FormikInputFormGroup id="name" name="name" label="Name:" />
            <FormikInputFormGroup id="latitude" name="latitude" label="Latitude:" />
            <FormikInputFormGroup id="longitude" name="longitude" label="Longitude:" />
          </Panel.Body>
          <Panel.Footer align="right">
            <NavLinkButton disabled={loading} kind="default" to="/landing/location/cities">
              Cancel
            </NavLinkButton>
            <Button loading={loading} kind="primary" type="submit">
              Save
            </Button>
          </Panel.Footer>
        </Panel>
      </FormikForm>
    </Formik>
  );
};

export const LandingLocationCityForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  useTitle(id ? 'Landing - Location - Cities - Edit' : 'Landing - Location - Cities - New');
  const navigate = useNavigate();
  const { data } = useLandingLocationCityQuery({
    client,
    fetchPolicy: 'network-only',
    variables: { id: id! },
    skip: !id,
  });
  const city = data?.city;

  const onSave = (city: Pick<Landing__Location__City, 'id'>) => {
    navigate(`/landing/location/cities/${city.id}`);
  };

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>Landing</Breadcrumb.Item>
          <Breadcrumb.Item>Location</Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to="/landing/location/cities">Cities</NavLink>
          </Breadcrumb.Item>
          {id && (
            <Breadcrumb.Item>
              <NavLink to={`/landing/location/cities/${id}`}>{city?.name ?? '-'}</NavLink>
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item active>{id ? 'Edit' : 'New'}</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      {!id || city ? <Fields city={city} onSave={onSave} /> : <Spinner />}
    </>
  );
};
